import {
  AuctionInsightKey,
  AuctionInsightsData,
  generateDashboardDemandSourcesOptions,
  getDashboardBidderAuctionInsightsSankeyData,
  getDashboardBidderAuctionInsightsTableData,
  IDashboardAuctionInsightsTableRow,
  IGetDashboardBidderAuctionInsightsSankeyDataQueryParams,
  IGetDashboardBidderAuctionInsightsTableData,
} from '~/src/dash/apiV2/auctionInsights';
import {
  generateBidderGroupsOptionsListV2,
  generateBiddersOptionsListV2,
} from '~/src/dash/apiV2/bidders';
import {
  generateChannelGroupsOptionsListV2,
  generateChannelsOptionsListV2,
} from '~/src/dash/apiV2/uiOptions';
import { ISankeyNodeConfig } from '~/src/dash/components/charts/Sankey/common';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import {
  convertSetIntoArrayOfNumbers,
  convertSetIntoArrayOfStrings,
} from '~/src/dash/components/filters/conversions';
import { IExtendedIFilterRowObject } from '~/src/dash/components/filters/FilterRowV2';
import { RadioButtonOption } from '~/src/dash/components/radio/RadioButtonSelector';
import {
  IListViewTableColumns,
  IListViewTablePagination,
  IListViewTableRow,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  EllipsisWithTooltipRender,
  MultiCurrencyRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { TKey } from '~/src/dash/publicaUI';
import { DateRange, TimeRange } from '~/src/dash/utils/dateTimeRanges';
import { bidderAuctionInsightsNodes } from '~/src/dash/views/bidder/BidderAuctionInsights/config';

export type AuctionInsightsDataset = 'bidders' | 'lineItems' | 'deals';

export interface IQueryParamConfig {
  filters: string[];
  customLogic?: boolean;
}

export interface IDashboardAuctionInsightsConfig {
  defaultState: {
    selectedDataset: AuctionInsightsDataset;
    selectedTimezone: string;
    selectedDateRange: DateRange;
    selectedTimeRange: TimeRange;
    isFilterMenuOpen: boolean;
    isPeriodMenuOpen: boolean;
    isRealtimeOnly: boolean;
    defaultRealTimeRange: TimeRange;
    defaultDateRange: DateRange;
  };
  datasets: Record<
    string,
    {
      value: AuctionInsightsDataset;
      tKey: string;
      enabledFilters: Array<keyof IDashboardAuctionInsightsConfig['filters']>;
      getSankeyData: (
        publisherID: string,
        query: unknown,
        signal?: AbortSignal,
      ) => Promise<AuctionInsightsData>;
      getTableData: (
        publisherID: string,
        query: unknown,
        signal?: AbortSignal,
      ) => Promise<IGetDashboardBidderAuctionInsightsTableData>;
      queryParams: Record<string, IQueryParamConfig>;
      nodesConfig: Partial<Record<AuctionInsightKey, ISankeyNodeConfig>>;
    }
  >;
  filters: Record<string, IExtendedIFilterRowObject>;
  table: {
    pagination: IListViewTablePagination;
    activeColumnKeys: string[];
    columnSelector: {
      resetKeys: string[];
    };
    columns: IListViewTableColumns;
  };
}

export const dashboardAuctionInsightsConfig: IDashboardAuctionInsightsConfig = {
  defaultState: {
    selectedDataset: 'bidders',
    selectedTimezone: '',
    selectedDateRange: 'Last 7 Days',
    selectedTimeRange: '24 Hours',
    isFilterMenuOpen: true,
    isPeriodMenuOpen: true,
    isRealtimeOnly: false,
    defaultRealTimeRange: '24 Hours',
    defaultDateRange: 'Last 7 Days',
  },
  datasets: {
    bidders: {
      value: 'bidders',
      tKey: 'Bidders',
      enabledFilters: ['DemandSources', 'Bidders', 'BidderGroups', 'Channels', 'ChannelGroups'],
      getSankeyData: getDashboardBidderAuctionInsightsSankeyData,
      getTableData: getDashboardBidderAuctionInsightsTableData,
      nodesConfig: bidderAuctionInsightsNodes,
      queryParams: {
        BidderIDs: {
          filters: ['Bidders', 'BidderGroups'],
        },
        SiteIDs: {
          filters: ['Channels', 'ChannelGroups'],
        },
        BidderCodes: {
          filters: ['DemandSources'],
          customLogic: true,
        },
      },
    },
  },
  filters: {
    DemandSources: {
      key: 'DemandSources',
      tKey: 'DemandSources',
      mutuallyExclusiveGroup: 'demand',
      getList: generateDashboardDemandSourcesOptions,
      selectedLimit: 0,
      extractStringToSort: (option: ISelectOption) => option.label,
      enableLabelTooltip: true,
      queryKey: 'BidderCodes',
      formatQueryValue: convertSetIntoArrayOfStrings, // technically this is not being used because we are using custom logic
      // we format the value into array of strings if it's a true demand source
      // if it's a custom demand source, we format the children IDs into an array of numbers which goes into BidderIDs
      hasCustomLogic: true,
      hasChildrenIDs: true,
    },
    Bidders: {
      key: 'Bidders',
      tKey: 'Bidders',
      mutuallyExclusiveGroup: 'demand',
      getList: generateBiddersOptionsListV2,
      selectedLimit: 100,
      extractStringToSort: (option: ISelectOption) => option.label,
      displayID: true,
      enableLabelTooltip: true,
      queryKey: 'BidderIDs',
      formatQueryValue: convertSetIntoArrayOfNumbers,
      enableActiveFilter: true,
      onLoadDisplayActiveOnly: true,
    },
    BidderGroups: {
      key: 'BidderGroups',
      tKey: 'BidderGroups',
      mutuallyExclusiveGroup: 'demand',
      getList: generateBidderGroupsOptionsListV2,
      selectedLimit: 1,
      extractStringToSort: (option: ISelectOption) => option.label,
      enableLabelTooltip: true,
      queryKey: 'BidderIDs',
      formatQueryValue: convertSetIntoArrayOfNumbers, // this will be overriden due to hasChildrenIDs
      hasChildrenIDs: true,
    },
    Channels: {
      key: 'Channels',
      tKey: 'Channels',
      mutuallyExclusiveGroup: 'inventory',
      getList: generateChannelsOptionsListV2,
      selectedLimit: 20,
      extractStringToSort: (option: ISelectOption) => option.label,
      displayID: true,
      enableLabelTooltip: true,
      queryKey: 'SiteIDs',
      formatQueryValue: convertSetIntoArrayOfNumbers,
      enableActiveFilter: true,
      onLoadDisplayActiveOnly: true,
    },
    ChannelGroups: {
      key: 'ChannelGroups',
      tKey: 'ChannelGroups',
      mutuallyExclusiveGroup: 'inventory',
      getList: generateChannelGroupsOptionsListV2,
      selectedLimit: 1,
      extractStringToSort: (option: ISelectOption) => option.label,
      enableLabelTooltip: true,
      queryKey: 'SiteIDs',
      formatQueryValue: convertSetIntoArrayOfNumbers, // this will be overriden due to hasChildrenIDs
      hasChildrenIDs: true,
    },
  },
  table: {
    pagination: {
      page: 0,
      rowsPerPage: 10,
      order: 'desc',
      orderBy: 'revenue',
    },
    activeColumnKeys: ['DemandSource', 'Revenue', 'BidRequests', 'BidResponses', 'Impressions'],
    columnSelector: {
      resetKeys: ['DemandSource', 'Revenue', 'BidRequests', 'BidResponses', 'Impressions'],
    },
    columns: {
      ID: {
        formatValue: (value: number) => value.toString(),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      Name: {
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name_link',
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        label: 'Name',
        tKey: 'Name',
        requestKey: 'name',
        responseKey: 'Name',
        valueType: 'string',
        orderByKey: 'name',
      },
      DemandSource: {
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        headerRenderType: 'string',
        key: 'DemandSource',
        label: 'Demand Source',
        tKey: 'DemandSource',
        orderByKey: 'bidder_type',
        requestKey: '',
        responseKey: 'DemandSource',
        valueType: 'string',
        disableSort: true,
      },
      Revenue: {
        cellJSX: MultiCurrencyRender,
        cellRenderType: 'number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        key: 'Revenue',
        label: 'Revenue',
        tKey: 'Revenue',
        requestKey: '',
        responseKey: 'Revenue',
        orderByKey: 'revenue',
        valueType: 'number',
      },
      BidRequests: {
        cellRenderType: 'large_number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        key: 'BidRequests',
        label: 'Bid Requests',
        tKey: 'BidRequests',
        orderByKey: 'bid_requests',
        requestKey: '',
        responseKey: 'BidRequests',
        valueType: 'number',
      },
      BidResponses: {
        cellRenderType: 'large_number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        key: 'BidResponses',
        label: 'Bid Responses',
        tKey: 'BidResponses',
        orderByKey: 'bid_responses',
        requestKey: '',
        responseKey: 'BidResponses',
        valueType: 'number',
      },
      Impressions: {
        cellRenderType: 'large_number',
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        key: 'Impressions',
        label: 'Impressions',
        tKey: 'Impressions',
        orderByKey: 'impressions',
        requestKey: '',
        responseKey: 'Impressions',
        valueType: 'number',
      },
    },
  },
};

export const getAuctionInsightsDatasetOptions = (): RadioButtonOption[] => {
  return Object.values(dashboardAuctionInsightsConfig.datasets).map((dataset) => ({
    value: dataset.value,
    tKey: dataset.tKey as TKey,
  }));
};

export const generateDashboardAuctionInsightsQueryParams = (
  selectedDataset: AuctionInsightsDataset,
  selectedFilters: Record<string, Set<string | number>>,
  selectedChildrenFilters: Record<string, Set<string | number>>,
  demandSourcesOptionsMap: Map<string, ISelectOption>,
  config: IDashboardAuctionInsightsConfig,
): Record<string, number[] | string[]> => {
  const queryParams: Record<string, number[] | string[]> = {};

  const datasetConfig = config.datasets[selectedDataset];
  const queryParamsConfig = datasetConfig.queryParams;

  Object.entries(queryParamsConfig).forEach(([paramKey, paramConfig]) => {
    // Handle custom logic for DemandSources
    if (paramConfig.customLogic && paramKey === 'BidderCodes') {
      const demandSourceIDs = selectedFilters.DemandSources || new Set();
      const bidderCodesSet = new Set<string>();
      const customBidderIDsSet = new Set<number>();

      demandSourceIDs.forEach((id) => {
        const option = demandSourcesOptionsMap.get(String(id));
        if (option?.childrenIDs && option.childrenIDs.length > 0) {
          // Custom demand source - add children IDs to BidderIDs
          option.childrenIDs.forEach((childID) => customBidderIDsSet.add(Number(childID)));
        } else if (option) {
          // Regular demand source - add code to BidderCodes
          bidderCodesSet.add(String(option.id));
        }
      });

      // Add BidderCodes to query params
      queryParams.BidderCodes = Array.from(bidderCodesSet);

      // Merge custom demand source bidder IDs with existing BidderIDs
      if (customBidderIDsSet.size > 0) {
        const existingBidderIDs = new Set<number>((queryParams.BidderIDs as number[]) || []);
        customBidderIDsSet.forEach((id) => existingBidderIDs.add(id));
        queryParams.BidderIDs = Array.from(existingBidderIDs);
      }
      return;
    }

    // Standard logic for non-custom params
    const combined = new Set<number | string>();
    let formatter:
      | ((value: Set<string | number>) => string | number | Array<string | number>)
      | null = null;

    paramConfig.filters.forEach((filterKey) => {
      const filter = config.filters[filterKey];

      // Auto-select formatter from first non-children filter
      if (!formatter && !filter.hasChildrenIDs) {
        formatter = filter.formatQueryValue;
      }

      // Collect IDs (children or direct)
      const ids = filter.hasChildrenIDs
        ? selectedChildrenFilters[filterKey]
        : selectedFilters[filterKey];

      if (ids) {
        ids.forEach((id) => combined.add(id));
      }
    });

    // Fallback: if all filters have children, use first filter's formatter
    if (!formatter && paramConfig.filters.length > 0) {
      formatter = config.filters[paramConfig.filters[0]].formatQueryValue;
    }

    // Apply formatter to combined Set
    if (formatter) {
      queryParams[paramKey] = formatter(combined) as number[] | string[];
    }
  });

  return queryParams;
};

export const getDashboardSankeyData = (
  publisherID: string,
  timezone: string,
  startDate: moment.Moment,
  endDate: moment.Moment,
  selectedDataset: AuctionInsightsDataset,
  selectedFilters: Record<string, Set<string | number>>,
  selectedChildrenFilters: Record<string, Set<string | number>>,
  demandSourcesOptionsMap: Map<string, ISelectOption>,
  config: IDashboardAuctionInsightsConfig,
  signal?: AbortSignal,
  isRealtimeOnly?: boolean,
  liveDensityRange?: string,
): Promise<AuctionInsightsData> => {
  // Build filter-specific params using shared helper
  const filterParams = generateDashboardAuctionInsightsQueryParams(
    selectedDataset,
    selectedFilters,
    selectedChildrenFilters,
    demandSourcesOptionsMap,
    config,
  );

  // Build full query params with common fields
  const queryParams = {
    PublisherID: Number(publisherID),
    Timezone: timezone,
    StartDate: startDate.format('YYYY-MM-DD HH:mm:ss'),
    EndDate: endDate.format('YYYY-MM-DD HH:mm:ss'),
    LiveDensityRange: liveDensityRange?.toString() || '',
    IsRealTimeOnly: isRealtimeOnly,
    ...filterParams,
  } as IGetDashboardBidderAuctionInsightsSankeyDataQueryParams;

  const datasetConfig = config.datasets[selectedDataset];

  // Call the getSankeyData function from the dataset config
  return datasetConfig.getSankeyData(publisherID, queryParams, signal);
};

export const formatAuctionInsightsTableDataIntoLVTRows = (
  rows: IDashboardAuctionInsightsTableRow[],
  cfg: IListViewTableColumns,
  demandSourceAdapterMap: Map<string, string>,
  bidderIdToDemandSourceMap: Map<number, string>,
): IListViewTableRow[] => {
  // Guard against undefined or null rows
  if (!rows || !Array.isArray(rows)) {
    return [];
  }

  const formattedRows = [] as IListViewTableRow[];
  rows.forEach((row) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      let value: unknown = row[responseKey as keyof IDashboardAuctionInsightsTableRow];

      // Special handling for DemandSource column
      if (responseKey === 'DemandSource' && value) {
        const rawValue = String(value);

        // 1. First check if this is a custom demand source (takes precedence)
        const customLabel = bidderIdToDemandSourceMap.get(row.ID);
        if (customLabel) {
          value = customLabel;
        } else {
          // 2. Try adapter map for standard transformations (e.g., "unruly" → "Nexxen CTRL")
          const adapterLabel = demandSourceAdapterMap.get(rawValue);
          value = adapterLabel || rawValue; // 3. Final fallback to raw value
        }
      }

      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};
